/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './DividendsCapitalGainsTable.scss';

import { array, number, object, shape } from 'prop-types';
import React, { Fragment, useState } from 'react';
import infoHelpIcon from 'assets/icons/info-help-icon-filled.svg';
import redInfoIcon from 'assets/icons/red-info-icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/warning-circle.svg';
import { ReactComponent as LockIcon } from 'assets/icons/lock-3.svg';
import { ReactComponent as Icon } from 'assets/icons/beacon/Information.svg';
import {
  DISTRIBUTE_TO_FUND_ID,
  DISTRIBUTE_TO_BANK_ID,
  TO_CHECK_ID,
  REINVEST_ID,
  CASH_ID,
  isPendingUpdate
} from 'services/Preferences';
import {AAMBeaconIconButton, AAMBeaconPopover, AAMBeaconTooltip} from 'components/shared/Beacon';
import { isMobileLayout } from 'state/view/ViewSelectors';
import SweepModalV2 from 'components/shared/Sweep/SweepModalV2';
import ShareClassConversionModal from 'components/shared/ShareClassConversion/ShareClassConversionModal';
import { shareClassConversionEnabled } from 'services/FeatureFlags';
import { updateDistributionToEdit } from 'state/preferences/PreferencesActions';
import DistributionEditModal from './DistributionEditModal';
import { updatePreferencesDividendsAndCapitalGains } from 'services/Rest';
import {
  EQUITY_ASSET,
  isActiveAdvisory,
  MUTUAL_FUND_ASSET
} from 'services/Products';
import DisclosureModal from 'components/shared/DisclosureModal/DisclosureModal';
import Content from 'services/Content';
import Store from 'state/Store';
import LocalBeaconTable from 'components/shared/Table/BeaconTable';
import { emptyToaster } from 'state/toast/ToastActions';
import BrokerageEquityAndETFModal from './BrokerageEquityAndETFModal';
import BrokerageMutualFundModal from './BrokerageMutualFundModal';
import RetirementIncome2020Modal from './RetirementIncome2020Modal';
import RetirementNotIncome2020Modal from './RetirementNotIncome2020Modal';
import NonRetirementNotIncome2020Modal from './NonRetirementNotIncome2020Modal';
import { checkIfMissingDistributionAccountId } from './DivCapUtils';
import { educationAuthTokenUrls } from 'services/Externals';

export function hasRetirementAccount(accounts, isRetirement) {
  return (
    isRetirement && accounts.some((account) => account.accountInfo.isIncome2020)
  );
}

export function getRegistrationName(owners, characterLimit, accountType, isTooltip){
  return (
      !isTooltip && owners.length > characterLimit ? (
      `${accountType} (${owners.substring(0, characterLimit)}...)`
      ) : (
        `${accountType} (${owners})`
      )
  );
}

const DistributionCellContent = ({ distribution }) => {
  const { distributionTypeId = -1 } = distribution;

  if (distributionTypeId === DISTRIBUTE_TO_BANK_ID) {
    return (
      <div className="distribution-bank-display-v2">
        <p className="distribution-account-name-v2">
          {distribution.bankInfo.bankName}
        </p>
        <p className="distribution-account-checkSum-v2 blocked_data">
          {distribution.bankInfo.bankAgreementKey.routingNumber} -{' '}
          {distribution.bankInfo.bankAgreementKey.lastFour}
        </p>
      </div>
    );
  } else if (distributionTypeId === DISTRIBUTE_TO_FUND_ID) {
    if(checkIfMissingDistributionAccountId(distribution)) {
      return (
          <div className="distribution-account-to-another-fund-display-v2 beacon-on-light">
            <span className="distribution-account-to-another-fund-display-text-v2 b-font-weight-light">To Another Fund</span>
            <AAMBeaconIconButton id="popover-edit-trigger" variant='tertiary' aria-label="editBeneficiary">
              <Icon slot="icon" />
            </AAMBeaconIconButton>
            <AAMBeaconPopover
                opens-by="#popover-edit-trigger"
                placement='top-end'>
              <div>
                <span>
                  To verify the receiving fund,<br /> please{' '}
                  <a className="beacon-on-dark" slot="action-zone" href={educationAuthTokenUrls.contactUrl}>
                    contact us
                  </a>.
                </span>
              </div>
            </AAMBeaconPopover>
          </div>
      );
    }
    else {
      return (
          <div className={'distribution-account-display-v2'}>
            <p className={'distribution-account-name-v2'}>
              {distribution.fundInfo.accountName} {distribution.fundInfo.ticker}
            </p>
            <p className={'distribution-account-checkSum-v2 blocked_data'}>
              {distribution.fundInfo.accountType}{' '}
              {distribution.fundInfo.accountType &&
              distribution.fundInfo.accountWChecksum ? (
                  <span className="spacing-dot"></span>
              ) : null}
              {distribution.fundInfo.accountWChecksum}
            </p>
          </div>
      );
    }
  } else if (distribution.distributionTypeId === TO_CHECK_ID) {
    return 'By Check';
  } else if (distribution.distributionTypeId === REINVEST_ID) {
    return 'Reinvest';
  } else if (distribution.distributionTypeId === CASH_ID) {
    return 'Cash';
  } else {
    return 'N/A';
  }
};

DistributionCellContent.propTypes = {
  distribution: shape({
    bankInfo: object,
    fundInfo: object,
    distributionTypeId: number.isRequired
  }).isRequired
};

const WithholdingPercentageCell = ({ withholdingRate }) => {
  if (withholdingRate) {
    return withholdingRate + '%';
  } else {
    return 'N/A';
  }
};

const handleEditClose = (selector) => {
  const modal = document.getElementById(selector) ?? null;
  if (modal !== null) {
    modal.close();
  }
};

const handleEditSave = (payload) => {
  updatePreferencesDividendsAndCapitalGains(payload);
};

const getDistributionsRow = ({
  accountInfo,
  capitalGains = {},
  dividends = {},
  isSharedClassFund,
  onEdit,
  isRetirement,
  isWithholding,
  withholdingRate,
  hasRetirementFunds,
  distributionToEdit,
  showModalClick
}) => {
  const { brokerageAssetType, isIncome2020 } = accountInfo;
  const handleOnClick = () => {
    Store.dispatch(emptyToaster());
    const distributionToEdit = {
      accountInfo,
      brokerageAssetType,
      capitalGains,
      dividends,
      isRetirement,
      isIncome2020,
      withholdingRate,
      isWithholding
    };
    Store.dispatch(updateDistributionToEdit(distributionToEdit));
    onEdit(distributionToEdit);
    showModalClick(true);
  };
  const showShareClassConversion =
    shareClassConversionEnabled() && isSharedClassFund;

  const isBrokerage =
    brokerageAssetType === 'EQUITY' || brokerageAssetType === 'MUTUAL_FUND';

  const hasError =
    isBrokerage && brokerageAssetType === 'EQUITY'
      ? !dividends?.distributionTypeId
      : !dividends?.distributionTypeId || !capitalGains?.distributionTypeId;

  const isPending =
    ((capitalGains && isPendingUpdate(capitalGains.pendingUpdateId)) ||
    (dividends && isPendingUpdate(dividends.pendingUpdateId))) &&
    accountInfo?.recordStatus === 'PENDING';

  const accountProductType =
    accountInfo && accountInfo.sourceAccountDescriptor
      ? accountInfo.sourceAccountDescriptor.productType
      : null;

  const baseRecords = [
    {
      record: (
        <div className="account-display-cell-v2">
          <div className={'account-ticker-v2 b-font-weight-bold'}>
            {accountInfo.ticker}
          </div>
          <div className={'account-name-v2 b-font-weight-bold'}>
            {accountInfo.accountName}
          </div>
          <div className={'account-checksum-v2 b-font-weight-light blocked_data'}>
            {accountInfo.accountWChecksum}
          </div>
          {/*sweep account, view only, share class conversion modals*/}
          {accountInfo.sweepAccount && (
            <div className="account-label-with-icon-v2">
              <SweepModalV2
                accountWChecksum={accountInfo.accountWChecksum}
                accountId={accountInfo.accountId}
              />
            </div>
          )}
          {accountInfo.isViewOnly && (
            <div
              className="account-label-v2 account-label-v2-sweep-account"
              aria-label="View Only">
              View Only
            </div>
          )}
          {isPending && (
            <div className="row-pending">
              <WarningIcon className="pending-icon" /> Pending Changes
            </div>
          )}
          {accountInfo.closedAccount && (
            <div
              className="account-label-v2 account-label-v2-sweep-account"
              aria-label="Closed">
              Closed
            </div>
          )}
          {showShareClassConversion && (
            <div className="account-label-with-icon-v2">
              <span
                className="account-label-v2 account-label-v2-sweep-account b-font-weight-bold"
                aria-label="Share Class Conversion">
                Share Class Conversion
              </span>
              <ShareClassConversionModal
                icon={<img src={infoHelpIcon} alt="info" />}
              />
            </div>
          )}
        </div>
      ),
      variant: 'text'
    },
    {
      record: hasError ? (
        <div className="div-caps-unavailable-v2">
          <img src={redInfoIcon} alt="info-icon" />
          <span className="div-caps-unavailable-v2-text">
            Currently unavailable
          </span>
        </div>
      ) : (
        <div className="fund-bank-cell-v2 b-font-weight-light">
          <DistributionCellContent distribution={dividends} />
        </div>
      ),
      variant: 'text'
    },
    {
      record: hasError ? (
        <div className="div-caps-unavailable-v2">
          <img src={redInfoIcon} alt="info-icon" />
          <span className="div-caps-unavailable-v2-text">
            Currently unavailable
          </span>
        </div>
      ) : brokerageAssetType === 'EQUITY' ? (
        <div className="fund-bank-cell-v2">N/A</div>
      ) : (
        <div className="fund-bank-cell-v2">
          <DistributionCellContent distribution={capitalGains} />
        </div>
      ),
      variant: 'text'
    },
    {
      record:
        hasError || isPending || accountInfo.isViewOnly ? (
          <div />
        ) : accountProductType !== null &&
          isActiveAdvisory(accountProductType) ? (
          <div className="edit-button-v2">
            <DisclosureModal
              icon={
                <LockIcon aria-label="lock" slot="suffix" />
              }
              closeOnClickOut={true}
              size={'Normal'}>
              <Fragment>
                <div className="b-typography-serif--headline--200">
                  Retirement Advisory Service
                </div>
                <div className="b-typography-sans-serif--body--200 b-font-weight-light">
                  {Content.rasDividendsAndCapitalGains}
                </div>
              </Fragment>
            </DisclosureModal>
          </div>
        ) : (
          <DistributionEditModal
            handleOnClick={handleOnClick}
            distributionToEdit={distributionToEdit}
            brokerageAssetType={brokerageAssetType}
            isIncome2020={isIncome2020}
            isRetirement={isRetirement}
            onClose={handleEditClose}
            onSave={handleEditSave}
          />
        ),
      variant: 'text'
    }
  ];

  const retirementRecord = {
    record: (
      <div className="fund-bank-cell-v2">
        <WithholdingPercentageCell withholdingRate={withholdingRate} />
      </div>
    ),
    variant: 'text'
  };

  return hasRetirementFunds
    ? [...baseRecords.slice(0, 3), retirementRecord, baseRecords[3]]
    : baseRecords;
};

const DividendsCapitalGainsTable = ({
  tableData,
  sharedClassConversionFunds
}) => {
  const [distributionToEdit, setDistributionToEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const characterLimit = isMobileLayout ? 30 : 12;

  const handleOnEdit = (distributionToEdit) => {
    setDistributionToEdit(distributionToEdit);
  };
  const currentModal = () => {
    if (distributionToEdit?.brokerageAssetType === EQUITY_ASSET) {
      return (
        <BrokerageEquityAndETFModal
          distributionToEdit={distributionToEdit}
          onBeaconClose={() => setShowModal(false)}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      );
    } else if (distributionToEdit?.brokerageAssetType === MUTUAL_FUND_ASSET) {
      return (
        <BrokerageMutualFundModal
          distributionToEdit={distributionToEdit}
          onBeaconClose={() => setShowModal(false)}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      );
    } else if (distributionToEdit?.isIncome2020) {
      return (
        <RetirementIncome2020Modal
          distributionToEdit={distributionToEdit}
          isRetirement={distributionToEdit?.isRetirement}
          onBeaconClose={() => setShowModal(false)}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      );
    } else if (
      distributionToEdit?.isRetirement &&
      !distributionToEdit?.isIncome2020
    ) {
      return (
        <RetirementNotIncome2020Modal
          distributionToEdit={distributionToEdit}
          onBeaconClose={() => setShowModal(false)}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      );
    } else {
      return (
        <NonRetirementNotIncome2020Modal
          distributionToEdit={distributionToEdit}
          onBeaconClose={() => setShowModal(false)}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      );
    }
  };
  return (
    <>
      {tableData.map((data, idx) => {
        const { accountType, accounts, isRetirement, owners } = data;
        const hasRetirementFunds = hasRetirementAccount(accounts, isRetirement);
        const baseHeaders = [
          {
            id: 'account-display-cell-header-v2',
            title: 'Accounts',
            variant: 'fixed'
          },
          {
            id: 'fund-bank-header-cell-v2',
            title: 'Dividends Payments',
            variant: 'group'
          },
          {
            id: 'fund-bank-header-cell-v2',
            title: 'Capital Gains Payments',
            variant: 'group'
          },
          {
            id: 'edit-header-v2',
            title: '',
            variant: 'group'
          }
        ];

        const retirementHeader = {
          id: 'fund-bank-header-cell-v2',
          title: 'Cash Withholding Percentage',
          variant: 'group'
        };

        return (
          <div className="dividends-capital-gains-table-v2" key={idx}>
            <div className="dividends-capital-gains-header-v2">
              <div className="dividends-capital-gains-registration-name-v2 blocked_data">
                  <span id={`tooltip-registration-header-trigger-${idx}`} className="dividends-capital-gains-registration-name-v2 b-font-weight-bold blocked_data">
                    {getRegistrationName(owners, characterLimit, accountType, false)}
                  </span>
                  {(owners.length > characterLimit) && (
                      <AAMBeaconTooltip
                        opens-by={`#tooltip-registration-header-trigger-${idx}`}
                        placement={'right'}
                        id="tooltip-content"
                      >
                        <div>{getRegistrationName(owners, characterLimit, accountType, true)}</div>
                      </AAMBeaconTooltip>
                  )}
              </div>
              <div className="dividends-capital-gains-container-v2">
                <LocalBeaconTable
                  renderStickyRows={true}
                  variant={'fixed'}
                  idName={'transactions-history-beacon-table'}
                  headers={
                    hasRetirementFunds
                      ? [
                          ...baseHeaders.slice(0, 3),
                          retirementHeader,
                          baseHeaders[3]
                        ]
                      : baseHeaders
                  }
                  rowsArray={accounts.map((account) => {
                    const {
                      accountInfo,
                      capitalGains,
                      dividends,
                      isWithholding,
                      withholdingRate
                    } = account;
                    const isSharedClassFund = sharedClassConversionFunds.some(
                      (sharedClassFund) =>
                        sharedClassFund.accountId === accountInfo.accountId &&
                        sharedClassFund.shareClassConversion
                          .activeShareClassConverted
                    );
                    return getDistributionsRow({
                      accountInfo,
                      capitalGains,
                      dividends,
                      isSharedClassFund,
                      isRetirement,
                      isWithholding,
                      withholdingRate,
                      onEdit: handleOnEdit,
                      account,
                      hasRetirementFunds,
                      distributionToEdit,
                      showModalClick: setShowModal
                    });
                  })}
                />
              </div>
            </div>
          </div>
        );
      })}
      {showModal && <>{currentModal()}</>}
    </>
  );
};

DividendsCapitalGainsTable.propTypes = {
  tableData: array.isRequired,
  sharedClassConversionFunds: array
};

export default DividendsCapitalGainsTable;
